import moment from "moment";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import { Box, Card, CardContent, Chip, IconButton, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BIGreen,
  BILightGreen,
  BILightRed,
  BILightYellow,
  BIRed,
  BIYellow
} from "../../assets/helpers";
import Loader from "../../common/Loader";
import { FETCH_CLIENT_EXPENSES } from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import { ClientEnglishFallback } from "../constant/ClientEnglishFallback";
import AddClientExpenses from "./ClientExpensesAddModal";
import ViewClientExpenses from "./ClientExpensesViewModal";

function ClientExpenses() {
  const location = useLocation();
  const dataId = location.state?.id;
  const navigate = useNavigate();

  const { loginData } = useSelector((state) => state?.main);
  const { selectedClientData, multiLanguageData } = useSelector((state) => state?.main);

  const [clientExpensesData, setClientExpensesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [currentClientExpenses, setCurrentClientExpenses] = useState();
  const [viewClientExpenses, setViewClientExpenses] = useState(false);
  const [viewAddModel, setViewAddModel] = useState(false);

  const LANGUAGE = ClientEnglishFallback.ClientExpenses(multiLanguageData?.ClientExpenses || {});
  const { TEXT_NO_EXPENSE } = LANGUAGE;

  useEffect(() => {
    if (typeof dataId === "number") {
      handleShowViewModel(dataId);
    }
    getClientExpensesData(1);
  }, []);

  const getClientExpensesData = async (pageNo) => {
    setIsLoading(true);
    try {
      const obj = {
        PageNr: pageNo,
        NrOfRecPerPage: 10,
        FullSearch: "",
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: true,
        SearchList: [
          {
            CreatedBy: loginData?.userInformation?.UserId,
            ClientEmployeeId: selectedClientData?.ClientEmployeeId
          }
        ],
        SortList: [
          {
            FieldName: "dateSubmitted",
            Direction: "DESC"
          }
        ],
        TypeOfObjectReturned: ""
      };
      const clientExpensesResponse = await Postrequestcall(
        FETCH_CLIENT_EXPENSES,
        obj,
        loginData?.accessToken
      );
      if (clientExpensesResponse.status === 200) {
        if (pageNo === 1) {
          const newData = groupedData(clientExpensesResponse?.data?.data);
          setClientExpensesData(newData);
        } else {
          const newData = groupedData(clientExpensesResponse?.data?.data);
          setClientExpensesData((prevData) => [...prevData, ...newData]);
        }
        setPageNo(pageNo + 1);
        if (clientExpensesResponse?.data?.data?.length === 0) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      }
    } catch (error) {
      console.log("getClientExpensesData-> Error Getting Client Expenses Data: ", error);
    }
    setIsLoading(false);
  };

  const groupedData = (finalData) => {
    let newData = [];

    finalData.forEach((item) => {
      let formattedDate = moment(item.dateSubmitted).format("MMMM DD YYYY");

      let existingEntry = newData.find((entry) => entry.date === formattedDate);

      if (existingEntry) {
        existingEntry.data.push(item);
      } else {
        // If it doesn't exist, create a new entry with this date and push the item.
        newData.push({
          date: formattedDate,
          data: [item]
        });
      }
    });

    // Sort items within each date group by dateModified (most recent first)
    newData.forEach((group) => {
      group.data.sort((a, b) => {
        const dateA = moment(a.dateModified || a.dateSubmitted);
        const dateB = moment(b.dateModified || b.dateSubmitted);
        return dateB.diff(dateA); // DESC order
      });
    });

    // Return the grouped data.
    return newData;
  };

  const handleShowViewModel = (data) => {
    setViewClientExpenses(true);
    setCurrentClientExpenses(data);
  };

  const handleHideViewModel = (status) => {
    if (typeof dataId === "number") {
      navigate("", { replace: true });
    }
    setViewClientExpenses(false);
    setCurrentClientExpenses([]);
    if (status === true) {
      getClientExpensesData(1);
    }
  };

  const handleToggleAddModel = async (load) => {
    setViewAddModel(!viewAddModel);
    if (load === true) {
      if (viewClientExpenses === true) {
        setViewClientExpenses(false);
      }
      await getClientExpensesData(1);
    }
    // Clear current expense data when opening add modal
    if (!viewAddModel) {
      setCurrentClientExpenses(null);
    }
  };

  const getColor = (status) => {
    switch (status) {
      case "in Review" && "In Review":
        return BIYellow;
      case "Rejected":
        return BIRed;
      case "Approved":
        return BIGreen;
      default:
        return "black"; // default color for unknown status
    }
  };

  const getBgColor = (status) => {
    switch (status) {
      case "in Review" && "In Review":
        return BILightYellow;
      case "Rejected":
        return BILightRed;
      case "Approved":
        return BILightGreen;
      default:
        return ""; // default color for unknown status
    }
  };

  const CustomListing = ({ data, index }) => {
    return (
      <Card
        key={index}
        sx={{
          cursor: "pointer",
          borderRadius: "10px",
          boxShadow: "none",

          marginBottom: "10px"
        }}
        onClick={() => handleShowViewModel(data.id)}>
        <CardContent>
          <Stack spacing={1}>
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {data.title}
              </Typography>
            </Stack>
            <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row" width="100%">
              <Typography variant="div" sx={{ color: "grey" }}>
                {data.clientExpenseCode}
                {" - "}
                {data.clientExpenseName}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {data.currencySign}
                {data.total ? data.total.toFixed(2) : ""}
              </Typography>
            </Stack>
            {data.status && (
              <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                <Chip
                  label={data.status}
                  sx={{
                    color: getColor(data.status),
                    backgroundColor: getBgColor(data.status)
                  }}
                />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Box>
        {isLoading && clientExpensesData?.length == 0 ? (
          <>{dataId ? <></> : <Loader fullScreen />}</>
        ) : (
          <>
            {clientExpensesData?.length === 0 && !isLoading && (
              <Stack sx={{ width: "100%", justifyContent: "center", alignItems: "center" }} height="85vh">
                <Typography>{TEXT_NO_EXPENSE}</Typography>
              </Stack>
            )}
            <InfiniteScroll
              dataLength={clientExpensesData?.length}
              next={() => getClientExpensesData(pageNo)}
              hasMore={hasMore}
              loader={<Loader />}
              height={"92vh"}
              overflow="auto"
              style={{ scrollbarWidth: "none" }}>
              {clientExpensesData?.map((data, index) => (
                <div key={index}>
                  <div
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      backgroundColor: "white",
                      borderRadius: "10px"
                    }}
                    key={index}>
                    <Card
                      sx={{
                        borderRadius: "10px",
                        marginBottom: "10px",
                        padding: "10px"
                      }}>
                      <Typography variant="div" sx={{ color: "grey" }}>
                        {data.date}
                      </Typography>
                    </Card>
                  </div>

                  {data.data?.map((item, index) => (
                    <CustomListing key={index} index={index} data={item} />
                  ))}
                </div>
              ))}
            </InfiniteScroll>
          </>
        )}
      </Box>
      {viewClientExpenses && (
        <ViewClientExpenses
          open={viewClientExpenses}
          handleClose={handleHideViewModel}
          currentClientExpensesId={currentClientExpenses}
          setViewAddModel={setViewAddModel}
          viewAddModel={viewAddModel}
        />
      )}
      {viewAddModel && (
        <AddClientExpenses
          open={viewAddModel}
          handleClose={handleToggleAddModel}
          currentClientExpensesId={currentClientExpenses}
        />
      )}
      <div className="button-div">
        <IconButton
          className="icon-button"
          sx={{ backgroundColor: selectedClientData?.AccentColor }}
          onClick={handleToggleAddModel}>
          <AddIcon className="add-icon" />
        </IconButton>
      </div>
    </>
  );
}

export default ClientExpenses;
